// src/pages/CaseStudyPage.js
// Premium case study detail page

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useSpring, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Factory,
  TrendingUp,
  Quote,
} from 'lucide-react';

// Layout components
import SmoothScroll from '../components/SmoothScroll';
import Navbar from '../components/Navbar';
import SEO, { generateArticleSchema } from '../components/SEO';

// Resource components
import RichTextRenderer from '../components/resources/RichTextRenderer';
import RelatedResources from '../components/resources/RelatedResources';

// Hooks
import { useCaseStudy } from '../hooks/useContentful';

// Styles
import './CaseStudyPage.css';

// Loading skeleton
const CaseStudyPageSkeleton = () => (
  <div className="case-study-skeleton">
    <div className="case-study-skeleton__header">
      <div className="case-study-skeleton__badge" />
      <div className="case-study-skeleton__title" />
      <div className="case-study-skeleton__meta" />
      <div className="case-study-skeleton__metrics">
        <div className="case-study-skeleton__metric" />
        <div className="case-study-skeleton__metric" />
        <div className="case-study-skeleton__metric" />
      </div>
    </div>
    <div className="case-study-skeleton__image" />
  </div>
);

// Metric card component
const MetricCard = ({ metric, index }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      className="case-study-page__metric"
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
      animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="case-study-page__metric-value">{metric.value}</span>
      <span className="case-study-page__metric-label">{metric.label}</span>
      {metric.description && (
        <span className="case-study-page__metric-desc">{metric.description}</span>
      )}
    </motion.div>
  );
};

const CaseStudyPage = () => {
  const { slug } = useParams();
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Fetch case study data
  const { data: caseStudy, loading, error } = useCaseStudy(slug);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <SmoothScroll>
        <Navbar />
        <main className="case-study-page">
          <CaseStudyPageSkeleton />
        </main>
      </SmoothScroll>
    );
  }

  if (error || !caseStudy) {
    return (
      <SmoothScroll>
        <Navbar />
        <main className="case-study-page">
          <div className="case-study-page__error">
            <h1>Case study not found</h1>
            <p>The case study you're looking for doesn't exist or has been moved.</p>
            <Link to="/resources" className="case-study-page__back-link">
              <ArrowLeft size={16} />
              <span>Back to Resources</span>
            </Link>
          </div>
        </main>
      </SmoothScroll>
    );
  }

  // Generate case study schema for SEO
  const caseStudySchema = generateArticleSchema({
    title: caseStudy.title,
    description: caseStudy.summary,
    image: caseStudy.heroImage?.url,
    publishedDate: caseStudy.publishedAt,
    url: `/case-studies/${slug}`,
  });

  return (
    <SmoothScroll>
      {/* SEO */}
      <SEO
        title={caseStudy.title}
        description={caseStudy.summary || `${caseStudy.clientName} case study: ${caseStudy.headlineOutcome || caseStudy.title}`}
        canonical={`/case-studies/${slug}`}
        image={caseStudy.heroImage?.url}
        type="article"
        article={{
          publishedTime: caseStudy.publishedAt,
        }}
        schema={caseStudySchema}
      />

      {/* Progress Bar */}
      <motion.div className="case-study-page__progress" style={{ scaleX }} />

      <Navbar />

      <main className="case-study-page">
        <article className="case-study-page__article">
          {/* Back link */}
          <div className="case-study-page__nav">
            <Link to="/resources" className="case-study-page__back-link">
              <ArrowLeft size={16} />
              <span>Back to Resources</span>
            </Link>
          </div>

          {/* Header */}
          <motion.header
            className="case-study-page__header"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
            animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Badge */}
            <span className="case-study-page__badge">Case Study</span>

            {/* Client Info */}
            <div className="case-study-page__client-info">
              {caseStudy.clientName && (
                <span className="case-study-page__client">
                  <Building2 size={16} />
                  {caseStudy.clientName}
                </span>
              )}
              {caseStudy.industry && (
                <span className="case-study-page__industry">
                  <Factory size={16} />
                  {caseStudy.industry}
                </span>
              )}
              {caseStudy.region && (
                <span className="case-study-page__region">
                  <MapPin size={16} />
                  {caseStudy.region}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="case-study-page__title">{caseStudy.title}</h1>

            {/* Headline Outcome */}
            {caseStudy.headlineOutcome && (
              <div className="case-study-page__outcome">
                <TrendingUp size={20} />
                <span>{caseStudy.headlineOutcome}</span>
              </div>
            )}

            {/* Intro */}
            {caseStudy.summary && (
              <p className="case-study-page__intro">{caseStudy.summary}</p>
            )}

            {/* Date */}
            <div className="case-study-page__date">
              Published {formatDate(caseStudy.publishedAt)}
            </div>
          </motion.header>

          {/* Key Metrics */}
          {caseStudy.metrics?.length > 0 && (
            <div className="case-study-page__metrics">
              {caseStudy.metrics.map((metric, index) => (
                <MetricCard key={metric.label} metric={metric} index={index} />
              ))}
            </div>
          )}

          {/* Hero Image */}
          {caseStudy.heroImage?.url && (
            <motion.figure
              className="case-study-page__hero"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                src={`${caseStudy.heroImage.url}?w=1400&q=85&fm=webp`}
                alt={caseStudy.heroImage.alt || caseStudy.title}
                className="case-study-page__hero-image"
              />
            </motion.figure>
          )}

          {/* Body Sections */}
          <div className="case-study-page__body">
            {/* Challenge Section */}
            {caseStudy.challenge?.json && (
              <section className="case-study-page__section">
                <h2 className="case-study-page__section-title">
                  <span className="case-study-page__section-number">01</span>
                  The Challenge
                </h2>
                <RichTextRenderer content={caseStudy.challenge} />
              </section>
            )}

            {/* Approach Section */}
            {caseStudy.approach?.json && (
              <section className="case-study-page__section">
                <h2 className="case-study-page__section-title">
                  <span className="case-study-page__section-number">02</span>
                  Our Approach
                </h2>
                <RichTextRenderer content={caseStudy.approach} />
              </section>
            )}

            {/* Results Section */}
            {caseStudy.results?.json && (
              <section className="case-study-page__section">
                <h2 className="case-study-page__section-title">
                  <span className="case-study-page__section-number">03</span>
                  Results & Impact
                </h2>
                <RichTextRenderer content={caseStudy.results} />
              </section>
            )}
          </div>

          {/* Testimonial */}
          {caseStudy.testimonial?.quote && (
            <motion.div
              className="case-study-page__testimonial"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Quote className="case-study-page__testimonial-icon" size={32} />
              <blockquote className="case-study-page__testimonial-quote">
                {caseStudy.testimonial.quote}
              </blockquote>
              <div className="case-study-page__testimonial-author">
                <span className="case-study-page__testimonial-name">
                  {caseStudy.testimonial.authorName}
                </span>
                {caseStudy.testimonial.authorTitle && (
                  <span className="case-study-page__testimonial-title">
                    {caseStudy.testimonial.authorTitle}
                    {caseStudy.testimonial.authorCompany && (
                      <>, {caseStudy.testimonial.authorCompany}</>
                    )}
                  </span>
                )}
              </div>
            </motion.div>
          )}

          {/* Technologies */}
          {caseStudy.technologies?.length > 0 && (
            <div className="case-study-page__tech">
              <h3 className="case-study-page__tech-title">Technologies Used</h3>
              <div className="case-study-page__tech-list">
                {caseStudy.technologies.map((tech) => (
                  <span key={tech.name} className="case-study-page__tech-item">
                    {tech.iconUrl && (
                      <img
                        src={tech.iconUrl}
                        alt={tech.name}
                        className="case-study-page__tech-icon"
                      />
                    )}
                    {tech.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Topics/Tags */}
          {caseStudy.topics?.length > 0 && (
            <div className="case-study-page__tags">
              <span className="case-study-page__tags-label">Topics:</span>
              <div className="case-study-page__tags-list">
                {caseStudy.topics.map((topic) => (
                  <span key={topic.id || topic.name} className="case-study-page__tag">
                    {topic.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Related Case Studies */}
        {caseStudy.relatedCaseStudies?.length > 0 && (
          <RelatedResources
            items={caseStudy.relatedCaseStudies}
            type="caseStudy"
            title="More success stories"
          />
        )}

        {/* Footer */}
        <footer className="case-study-page__footer">
          <div className="case-study-page__footer-container">
            <p>© 2025 Rosalytics. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </SmoothScroll>
  );
};

export default CaseStudyPage;
